import { useState } from 'react'
import { useOrganizations, useCreateOrganization, useUpdateOrganization, useDeleteOrganization } from '@/hooks/useOrganizations'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EmptyState } from '@/components/profile/EmptyState'
import { Building2, Plus, Pencil, Trash2 } from 'lucide-react'

type FormData = { id?: string; organization_name: string; role: string; start_date: string; end_date: string; is_active: boolean; notes: string }
const empty: FormData = { organization_name: '', role: '', start_date: '', end_date: '', is_active: true, notes: '' }

export function OrganizationsPage() {
  const { data: items = [], isLoading } = useOrganizations()
  const create = useCreateOrganization()
  const updateOrg = useUpdateOrganization()
  const deleteOrg = useDeleteOrganization()
  const [form, setForm] = useState<FormData | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const openEdit = (item: Record<string, unknown>) => setForm({
    id: item.id as string, organization_name: item.organization_name as string,
    role: (item.role as string) ?? '', start_date: (item.start_date as string) ?? '',
    end_date: (item.end_date as string) ?? '', is_active: item.is_active as boolean, notes: (item.notes as string) ?? '',
  })

  const handleSave = () => {
    if (!form || !form.organization_name) { setFormError('Organization name is required'); return }
    setFormError(null)
    const payload = {
      organization_name: form.organization_name, role: form.role || null,
      start_date: form.start_date || null, end_date: form.end_date || null,
      is_active: form.is_active, notes: form.notes || null,
    }
    if (form.id) {
      updateOrg.mutate({ id: form.id, ...payload }, { onSuccess: () => setForm(null) })
    } else {
      create.mutate(payload, { onSuccess: () => setForm(null) })
    }
  }

  const showWarning = form && form.end_date && form.is_active

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" onClick={() => setForm({ ...empty })}><Plus className="h-3 w-3" /> Add Organization</Button></div>

      {items.length === 0 ? (
        <EmptyState icon={Building2} title="No organizations yet" description="Add your affiliations and memberships." actionLabel="Add Organization" onAction={() => setForm({ ...empty })} />
      ) : items.map((item) => (
        <Card key={item.id}>
          <CardContent className="flex items-start justify-between pt-4">
            <div>
              <p className="font-medium text-sm">{item.organization_name}</p>
              {item.role && <p className="text-xs text-muted-foreground">{item.role}</p>}
              <span className={`inline-block mt-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                {item.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => deleteOrg.mutate(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setForm(null)}>
          <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 font-semibold">{form.id ? 'Edit' : 'Add'} Organization</h3>
            {formError && <p className="mb-2 text-xs text-red-600">{formError}</p>}
            <div className="space-y-3">
              <div className="space-y-1"><Label>Organization Name *</Label><Input value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Member, Treasurer" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div className="space-y-1"><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-input" /> Currently active</label>
              {showWarning && <p className="text-xs text-amber-600">You have an end date but marked active. Did you mean to mark inactive?</p>}
              <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button className="w-full" onClick={handleSave} disabled={create.isPending || updateOrg.isPending}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
